import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';

/**
 * A `⋮` overflow menu for a row's actions.
 *
 * Same dismissal contract as AccountMenu (click-outside + Escape), lifted into its
 * own component because a row can carry several of these — the Agents page renders
 * one per cluster.
 *
 * The panel is PORTALLED to the body and positioned fixed, rather than absolutely
 * inside the trigger. Its first home was a normal absolute child, which rendered
 * perfectly and was invisible: `.agent-group` sets `overflow: hidden` to clip its
 * table to the rounded border, and that clips a dropdown too. Escaping the subtree
 * is cheaper and less fragile than unpicking every ancestor's overflow.
 *
 * The trade of `position: fixed` is that the panel does not travel with the page,
 * so scrolling closes it — the same thing every native menu does.
 *
 * Items close the menu on click. `disabled` items do NOT: a disabled action here
 * still carries the title that explains WHY it is unavailable ("Commands are
 * disabled for this cluster"), and closing the menu out from under that hover would
 * hide the only explanation the operator gets.
 */
export function KebabMenu({
  label = 'More actions',
  children,
}: {
  label?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Measured before paint so the panel never shows at 0,0 for a frame.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const close = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    // Capture phase: the page scrolls in a nested container here, not on window.
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  return (
    <div className="kebab-menu">
      <button
        ref={triggerRef}
        type="button"
        className="kebab-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="more" size={16} />
      </button>
      {open &&
        pos &&
        createPortal(
          // Click-capture on the container rather than a wrapper per item: an enabled
          // item closes the menu whatever it is (button, Link), a disabled one is
          // inert and never reaches here.
          <div
            ref={panelRef}
            className="kebab-panel"
            role="menu"
            style={{ top: pos.top, right: pos.right }}
            onClick={(e) => {
              const el = (e.target as HTMLElement).closest('button, a');
              if (el && !el.hasAttribute('disabled')) setOpen(false);
            }}
          >
            {children}
          </div>,
          document.body,
        )}
    </div>
  );
}
